import { useMemo, useState } from 'react';
import * as THREE from 'three';
import { type ThreeEvent } from '@react-three/fiber';
import { useSketchStore } from '../../state/useSketchStore';
import { looseEdgeSelectionId } from '../../core/sketch/types';
import { faceMaterialProps } from './faceMaterial';
import { SKETCH_COLORS } from '../../themes/colors';
import { emitSketchDown, emitSketchMove, type SketchHit } from '../../tools/sketch/sketchToolBus';
import type { SketchLooseEdge } from '../../core/sketch/types';

/**
 * Sketch loose edge layer — drawn segments not yet absorbed into a face.
 *
 * Sketch semantics: a loose edge is a first-class pickable entity. The visual
 * is one merged lineSegments; each edge additionally carries an invisible
 * pick cylinder so the pointer can grab the line itself (Erase removes it,
 * the Tape Measure arms a parallel guide off it, drawing snaps to it via the
 * inference pools).
 */

const PICK_RADIUS = 0.05;
const UP = new THREE.Vector3(0, 1, 0);

function LooseEdgePick({
    edgeId,
    a,
    b,
    onHover,
}: {
    edgeId: string;
    a: THREE.Vector3;
    b: THREE.Vector3;
    onHover: (edgeId: string | null) => void;
}) {
    const { mid, quat, length } = useMemo(() => {
        const dir = b.clone().sub(a);
        const len = Math.max(dir.length(), 1e-6);
        return {
            mid: a.clone().add(b).multiplyScalar(0.5),
            quat: new THREE.Quaternion().setFromUnitVectors(UP, dir.normalize()),
            length: len,
        };
    }, [a, b]);

    const hitOf = (e: ThreeEvent<PointerEvent>): SketchHit => ({
        kind: 'edge',
        edgeId,
        point: e.point.clone(),
        shiftKey: e.nativeEvent.shiftKey,
        ctrlKey: e.nativeEvent.ctrlKey,
        altKey: e.nativeEvent.altKey,
        button: e.button,
        pointerPx: { x: e.nativeEvent.clientX, y: e.nativeEvent.clientY },
    });

    return (
        <mesh
            position={mid}
            quaternion={quat}
            visible={false}
            onPointerDown={(e: ThreeEvent<PointerEvent>) => {
                if (e.button !== 0) return;
                e.stopPropagation();
                emitSketchDown(hitOf(e));
            }}
            onPointerMove={(e: ThreeEvent<PointerEvent>) => {
                e.stopPropagation();
                emitSketchMove(hitOf(e));
            }}
            onPointerOver={(e: ThreeEvent<PointerEvent>) => {
                e.stopPropagation();
                onHover(edgeId);
            }}
            onPointerOut={() => onHover(null)}
        >
            <cylinderGeometry args={[PICK_RADIUS, PICK_RADIUS, length, 6]} />
        </mesh>
    );
}

export function SketchLooseEdgeLayer() {
    const edges = useSketchStore((s) => s.edges);
    const selectedIds = useSketchStore((s) => s.selectedIds);
    const displayStyle = useSketchStore((s) => s.displayStyle);
    const [hoveredEdgeId, setHoveredEdgeId] = useState<string | null>(null);
    const style = faceMaterialProps(displayStyle, undefined, 'concrete');

    const all = Object.values(edges);
    const geometry = useMemo(() => {
        const pts: number[] = [];
        all.forEach((edge: SketchLooseEdge) => {
            pts.push(edge.a[0], edge.a[1], edge.a[2], edge.b[0], edge.b[1], edge.b[2]);
        });
        const geo = new THREE.BufferGeometry();
        geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(pts), 3));
        return geo;
    }, [edges]);

    const hovered = hoveredEdgeId ? edges[hoveredEdgeId] : undefined;
    const hoverGeometry = useMemo(() => {
        if (!hovered) return null;
        const geo = new THREE.BufferGeometry();
        geo.setAttribute(
            'position',
            new THREE.BufferAttribute(
                new Float32Array([
                    hovered.a[0], hovered.a[1], hovered.a[2],
                    hovered.b[0], hovered.b[1], hovered.b[2],
                ]),
                3
            )
        );
        return geo;
    }, [hovered]);

    // Selected segments render the solid-edge layer's selection language
    // (Sketch's blue selection) as an overlay above the ink lines.
    const selectedGeometry = useMemo(() => {
        const selected = new Set(selectedIds);
        const picked = all.filter((edge) => selected.has(looseEdgeSelectionId(edge.id)));
        if (picked.length === 0) return null;
        const pts: number[] = [];
        picked.forEach((edge) => {
            pts.push(edge.a[0], edge.a[1], edge.a[2], edge.b[0], edge.b[1], edge.b[2]);
        });
        const geo = new THREE.BufferGeometry();
        geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(pts), 3));
        return geo;
    }, [all, selectedIds]);

    return (
        <group>
            {all.length > 0 && (
                <lineSegments geometry={geometry} raycast={() => null}>
                    {/* Loose edges stay visible in every style — they are
                        first-class pickable entities, not face borders. */}
                    <lineBasicMaterial color={style.edgeColor} />
                </lineSegments>
            )}
            {selectedGeometry && (
                <lineSegments geometry={selectedGeometry} raycast={() => null} renderOrder={6}>
                    <lineBasicMaterial color={SKETCH_COLORS.faceSelected} depthTest={false} transparent opacity={0.95} />
                </lineSegments>
            )}
            {hovered && hoverGeometry && (
                <lineSegments geometry={hoverGeometry} raycast={() => null} renderOrder={6}>
                    <lineBasicMaterial color={SKETCH_COLORS.primary} depthTest={false} transparent opacity={0.95} />
                </lineSegments>
            )}
            {all.map((edge) => (
                <LooseEdgePick
                    key={edge.id}
                    edgeId={edge.id}
                    a={new THREE.Vector3(...edge.a)}
                    b={new THREE.Vector3(...edge.b)}
                    onHover={setHoveredEdgeId}
                />
            ))}
        </group>
    );
}
